import _ from 'lodash'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import api from '../../services/api'
import { REPORT_TYPE_ID_MAP, REPORT_REPORT_TYPE_NAME_MAP } from '../../constants/report'
import useDirtyPrompt from '../../hooks/useDirtyPrompt'
import useModal from '../../hooks/useModal'

import BasicPieChart from '../../components/charts/PieChart'
import BasicLineChart from '../../components/charts/LineChart'
import { BasicBtn, UpdateAttendanceBtn } from '../../components/Buttons'
import { ContentsBox, FlexItemBox } from '../../components/main/Main.styles'
import { ProgressBar } from '../../components/ProgressBar'
import TextField from '@mui/material/TextField'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'

import { ReactComponent as ReportIcon } from '../../assets/images/menu/report_icon.svg'

export default function ModifyReport() {
  const [attendanceInfo, setAttendanceInfo] = useState()
  const [etc, setEtc] = useState([''])
  const [goodPoints, setGoodPoints] = useState([''])
  const [improvePoints, setImprovePoints] = useState([''])
  const [statics, setStatics] = useState()
  const { openModal } = useModal()
  const { updateIsDirty, setIsDirty } = useDirtyPrompt()
  const { reportId } = useParams()
  const [query, setQuery] = useSearchParams()
  const navigate = useNavigate()
  const reportTypeId = parseInt(query.get('reportTypeId'))
  const goodPointRefs = useRef([])
  const improvePointRefs = useRef([])
  const etcRefs = useRef([])
  const date = moment(query.get('date')).format('YYYY-MM-DD')

  useEffect(() => {

    api.get(`/attendances/${date}`)
      .then(result => setAttendanceInfo(result.data))
      .catch(e => {
        console.error(e)
        openModal({ type: 'alert', props: {isError: true, contents: e.response?.data.message || e.message }})
      })

    api.get(`/reports/infos/performances/${date}`, { params: { reportTypeId } })
      .then(result => setStatics(result.data.statics))
      .catch(e => {
        console.error(e)
        openModal({ type: 'alert', props: {isError: true, contents: e.response?.data.message || e.message }})
      })

    api.get(`/reports/${date}/${reportId}`)
      .then(result => {
        const { feedback } = result.data
        setGoodPoints(feedback.goodPoints)
        setImprovePoints(feedback.improvePoints)
        setEtc(feedback.etc)
      })
      .catch(e => {
        console.error(e)
        openModal({ type: 'alert', props: {isError: true, contents: e.response?.data.message || e.message }})
      })
  }, [])

  function handleKeyDown(e, idx, state, setState, refs, type) {
    if (e.key === 'Enter' && !e.shiftKey) { // Enter만 눌렀을 때
      e.preventDefault() // 기존 줄바꿈 방지
      const newInputs = [...state]
      newInputs.splice(idx + 1, 0, '')
      setState(newInputs)

      updateIsDirty(
        type === 'good' ? newInputs : goodPoints,
        type === 'improve' ? newInputs : improvePoints,
        type === 'etc' ? newInputs : etc
      )

      // 새 input으로 포커스 이동
      setTimeout(() => {
        refs.current[idx + 1]?.focus()
      }, 0)
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && state[idx].length === 0 && state.length > 1) {
      e.preventDefault() // 기존 이벤트(Backspace, Delete)가 바로 아래 input에 전달되지 않게 막음

      const newInputs = [...state]
      newInputs.splice(idx, 1)
      setState(newInputs)

      setTimeout(() => {
        refs.current[Math.max(0, idx - 1)]?.focus()
      }, 0)

      updateIsDirty(
        type === 'good' ? newInputs : goodPoints,
        type === 'improve' ? newInputs : improvePoints,
        type === 'etc' ? newInputs : etc
      )
    }
  }

  function handleInputChange(e, idx, state, setState, type) {
    const newInputs = [...state]
    newInputs[idx] = e.target.value
    setState(newInputs)
    updateIsDirty(
      type === 'good' ? newInputs : goodPoints,
      type === 'improve' ? newInputs : improvePoints,
      type === 'etc' ? newInputs : etc
    )
  }

  return (
    <ContentsLayout>
      <ReportIcon width='35px' fill='black' style={{ verticalAlign: 'middle', marginRight: '10px' }}/>
      <span style={{ fontWeight: 'bold', fontSize: '1.5em', marginLeft: '10px' }}>{date} {REPORT_REPORT_TYPE_NAME_MAP[reportTypeId]} 리포트</span>
      <BasicBtn
        height='40px'
        width='120px'
        onClick={async () => {
          setIsDirty(false)
          const result = await api.put(`/reports/${date}/${reportId}`,
            {
              reportTypeId,
              feedback: {
                goodPoints,
                improvePoints,
                etc,
              }
            }
          )
          .catch(e => {
            console.error(e)
            openModal({ type: 'alert', props: { isError: true, contents: e.response?.data.message || e.message }})
          })

          if (result?.status === 200) {
            setIsDirty(false)
            navigate(`../${reportId}?reportTypeId=${reportTypeId}&date=${date}`)
          }
        }}
        style={{ position: 'absolute', right: 0 }}
      >완료
      </BasicBtn>
      <FlexContainer>
        <FlexContentsBox margin='10px 10px 0 0' width={REPORT_TYPE_ID_MAP.DAILY === reportTypeId ? '80%' : '100%'}>
          <FlexContainer style={{ justifyContent: 'center' }}>
            <ul style={{ width: REPORT_TYPE_ID_MAP.DAILY === reportTypeId ? '60%' : '100%', lineHeight: '2.5em', fontWeight: 'bold', marginTop: '10px'}}>
              <li style={{ verticalAlign: 'middle'}}>
                달성률
                <ProgressBar margin='0 10px 0 10px' max='100' value={statics?.completionRate || 0} width='65%' height='15px' />
                <span style={{ fontSize: '0.8em' }}>{statics?.completionRate}%</span>
              </li>
              <li>
                집중도
                <ProgressBar margin='0 10px 0 10px' max='100' value={statics?.intensiveRate || 0} width='65%' height='15px'/>
                <span style={{ fontSize: '0.8em' }}>{statics?.intensiveRate}%</span>
              </li>
              <li>총 {statics?.totalIntensiveTime?.hour}시간 {statics?.totalIntensiveTime?.min}분 학습 {REPORT_TYPE_ID_MAP.DAILY === reportTypeId ? `(${statics?.studyTime})` : ''} </li>
              {
                REPORT_TYPE_ID_MAP.DAILY === reportTypeId
                  ?
                  <li>
                    {
                      _.isEmpty(attendanceInfo)
                        ? '출석 인증 안 함'
                        :
                        attendanceInfo.isLate
                          ?
                          <>
                            <span style={{ color: 'red' }}>지각</span> [{moment(attendanceInfo.createdAt).format('YYYY-MM-DD HH:mm:ss')}]
                          </>
                          : <>
                            <span style={{ color: 'blue' }}>출석</span> [{moment(attendanceInfo.createdAt).format('YYYY-MM-DD HH:mm:ss')}]
                          </>
                    }
                  </li>
                  :
                  <></>
              }
            </ul>
            {
              REPORT_TYPE_ID_MAP.DAILY === reportTypeId
                ?
                <UpdateAttendanceBtn
                  attendanceInfo={attendanceInfo}
                  setAttendanceInfo={setAttendanceInfo}
                  date={date}
                  width='30%'
                />
                :
                <></>

            }
          </FlexContainer>
        </FlexContentsBox>
        {
          REPORT_TYPE_ID_MAP.DAILY !== reportTypeId
            ?
            <FlexContentsBox margin='10px 10px 0 0' width='100%' height= '200px'>
              {
                <BasicLineChart
                  data={[
                    {
                      id: '학습 시간',
                      data: _.map(statics?.studyStatsByPeriod, v => ({ x: moment(v.date).format('MM-DD'), y: (v.studyTime.hour) * 60 + (v.studyTime.min) }))
                    },
                  ]}
                  max={_.every(statics?.studyStatsByPeriod, v => v.studyTime.hour * 60 + v.studyTime.min === 0) ? '100' : undefined}
                  yAxis='분'
                  margin={{ top: 20, right: 90, bottom: 30, left: 50 }}
                  colors='#e5d8bd'
                  isAxisBottom={!(REPORT_TYPE_ID_MAP.DAILY !== reportTypeId && REPORT_TYPE_ID_MAP.WEEKLY !== reportTypeId)}
                  pointSize={REPORT_TYPE_ID_MAP.DAILY !== reportTypeId && REPORT_TYPE_ID_MAP.WEEKLY !== reportTypeId ? 0 : 5}
                />
              }
            </FlexContentsBox>
            :
            <></>
        }
        <FlexContentsBox margin='10px 0 0 0' width={REPORT_TYPE_ID_MAP.DAILY === reportTypeId ? '50%' : '100%'} height= '200px' >
          {
            REPORT_TYPE_ID_MAP.DAILY === reportTypeId
              ? <BasicPieChart
                data={_.map(statics?.timeTrackingStatics, ts => ({ id: ts.dailyLogTypeNameKo, value: ts.usageRate }))}
                margin={{ top: 30, bottom: 30 }}
              />
              :
              <BasicLineChart
                data={[
                  {
                    id: '달성률',
                    data: _.map(statics?.studyStatsByPeriod, ss => ({ x: moment(ss.date).format('MM-DD'), y: ss.completionRate }))
                  },
                  {
                    id: '집중도',
                    data: _.map(statics?.studyStatsByPeriod, ss => ({ x: moment(ss.date).format('MM-DD'), y: ss.intensiveRate }))
                  }
                ]}
                max='100'
                yAxis='%'
                margin={{ top: 20, right: 90, bottom: 30, left: 50 }}
                isAxisBottom={!(REPORT_TYPE_ID_MAP.DAILY !== reportTypeId && REPORT_TYPE_ID_MAP.WEEKLY !== reportTypeId)}
                pointSize={REPORT_TYPE_ID_MAP.DAILY !== reportTypeId && REPORT_TYPE_ID_MAP.WEEKLY !== reportTypeId ? 0 : 5}
              />
          }
        </FlexContentsBox>
      </FlexContainer>
      <FlexContainer>
        <FlexContentsBox margin='10px 10px 0 0' width='100%' alignSelf='flex-start'>
          <ThumbUpOffAltIcon style={{ verticalAlign: 'middle', marginRight: '10px', width: '20px' }}/>
          <b>잘한점</b>
          {goodPoints.map((goodPoint, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <span style={{ width: '30px', textAlign: 'right', marginRight: '8px' }}>{idx + 1}.</span>
              <TextField
                id='good-point'
                value={goodPoint}
                onKeyDown={(e) => handleKeyDown(e, idx, goodPoints, setGoodPoints, goodPointRefs, 'good')}
                onChange={(e) => handleInputChange(e, idx, goodPoints, setGoodPoints, 'good')}
                inputRef={(el) => (goodPointRefs.current[idx] = el)}
                multiline
                fullWidth
              />
            </div>
          ))}
        </FlexContentsBox>
        <FlexContentsBox margin='10px 10px 0 0' width='100%' alignSelf='flex-start'>
          <ThumbDownOffAltIcon style={{ verticalAlign: 'middle', marginRight: '10px', width: '20px' }}/>
          <b>아쉬운점 → 본질적인 원인 → 해결책</b>
          {improvePoints.map((improvePoint, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <span style={{ width: '30px', textAlign: 'right', marginRight: '8px' }}>{idx + 1}.</span>
              <TextField
                key={idx}
                id='improve-point'
                value={improvePoint}
                onKeyDown={(e) => handleKeyDown(e, idx, improvePoints, setImprovePoints, improvePointRefs, 'improve')}
                onChange={(e) => handleInputChange(e, idx, improvePoints, setImprovePoints, 'improve')}
                inputRef={(el) => (improvePointRefs.current[idx] = el)}
                multiline
                fullWidth
                style={{ marginBottom: '8px' }}
              />
            </div>
          ))}
        </FlexContentsBox>
        <FlexContentsBox margin='10px 0 0 0' width='100%' alignSelf='flex-start'>
          <InfoOutlinedIcon style={{ verticalAlign: 'middle', marginRight: '10px', width: '20px' }}/>
          <b>기타</b>
          {etc.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
              <span style={{ width: '30px', textAlign: 'right', marginRight: '8px' }}>{idx + 1}.</span>
              <TextField
                key={idx}
                id='etc'
                value={item}
                onKeyDown={(e) => handleKeyDown(e, idx, etc, setEtc, etcRefs, 'etc')}
                onChange={(e) => handleInputChange(e, idx, etc, setEtc, 'etc')}
                inputRef={(el) => (etcRefs.current[idx] = el)}
                multiline
                fullWidth
                style={{ marginBottom: '8px' }}
              />
            </div>
          ))}
        </FlexContentsBox>
      </FlexContainer>
    </ContentsLayout>
  )
}

export const ContentsLayout = styled.div`
  margin: 20px;
  position: relative;
`

const FlexContainer = styled.div`
  display: flex;
`

const FlexContentsBox = styled(ContentsBox, FlexItemBox)`
  align-self: ${(props) => props.alignSelf};
  word-break:break-all;
`